import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { IconButton, Tooltip } from '@material-ui/core';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ConfirmDialog from '../ConfirmDialog';
import useVrackApi from '../../hooks/useVrackApi';
import { setOpenSnackbar } from '../../store/slices/snackbarSlice';

DeleteCompanyButton.propTypes = {
  company: PropTypes.object.isRequired,
  // No-arg callback invoked after a successful delete — typically the
  // parent's `refetch` for the companies list.
  onDeleted: PropTypes.func
};

DeleteCompanyButton.defaultProps = {
  onDeleted: null
};

export default function DeleteCompanyButton({ company, onDeleted }) {
  const [confirmDialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const { fetchData: deleteCompanyApi } = useVrackApi('delete', '/admin/companies');

  const handleDialogCancel = () => {
    if (loading) return;
    setDialogOpen(false);
  };

  const handleDialogConfirm = async () => {
    setLoading(true);
    try {
      await deleteCompanyApi({ data: { company_id: company.id } });
      openSnackbar({ message: `${company.name} deleted successfully.` });
      if (onDeleted) onDeleted();
      setDialogOpen(false);
    } catch (error) {
      openSnackbar({ message: 'Failed to delete company.', severity: 'error' });
    }
    setLoading(false);
  };

  const handleDialogOpen = (event) => {
    event.stopPropagation();
    setDialogOpen(true);
  };

  return (
    <>
      <ConfirmDialog
        title="Delete company?"
        content={`This will remove ${company.name} from the company list. This action cannot be undone.`}
        onCancelHandler={handleDialogCancel}
        onConfirmHandler={handleDialogConfirm}
        isOpen={confirmDialogOpen}
      />
      <Tooltip title={`Delete ${company.name}`}>
        <IconButton onClick={handleDialogOpen} color="primary" disabled={loading}>
          <DeleteOutlineIcon data-testid="delete-company-icon" />
        </IconButton>
      </Tooltip>
    </>
  );
}
